import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { authAPI } from '../../services/api';
import OtpModal from './OtpModal';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingPassword, setPendingPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await authAPI.login({ email, password });
      
      // Check if OTP is required (Super Admin 2FA)
      if ((response as any).requiresOtp) {
        setPendingEmail(email);
        setPendingPassword(password);
        setShowOtpModal(true);
        setIsLoading(false);
        return;
      }

      // Regular login - store token and user
      const userWithRoles = { ...response.user, roles: response.roles };
      sessionStorage.setItem('token', response.token);
      sessionStorage.setItem('user', JSON.stringify(userWithRoles));
      
      // Force reload to update auth context
      window.location.href = userWithRoles.isSuperAdmin ? '/superadmin' : '/dashboard';
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerify = async (otp: string) => {
    setOtpError('');
    setIsLoading(true);

    try {
      const response = await authAPI.verifyOtp({ email: pendingEmail, otp, password: pendingPassword });
      
      // Store token and user
      const userWithRoles = { ...response.user, roles: response.roles };
      sessionStorage.setItem('token', response.token);
      sessionStorage.setItem('user', JSON.stringify(userWithRoles));
      
      setShowOtpModal(false);
      // Force reload to update auth context
      window.location.href = '/superadmin';
    } catch (err: any) {
      setOtpError(err.response?.data?.message || 'Invalid or expired OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      await authAPI.resendOtp(pendingEmail);
      setOtpError('');
    } catch (err: any) {
      setOtpError(err.response?.data?.message || 'Failed to resend OTP');
    }
  };

  const handleCancelOtp = () => {
    setShowOtpModal(false);
    setOtpError('');
  };

  return (
    <>
      <Helmet>
        <title>GymsSync Login - Access Your Gym Management Dashboard</title>
        <meta name="description" content="Login to GymsSync - Best gym management software in India. Access your dashboard to manage members, attendance, payments, and more." />
        <meta name="keywords" content="gymssync login, gymsync login, gym management software login, gym software login India, gymsync dashboard, gym management system login, fitness software login, gym ERP login" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={typeof window !== 'undefined' ? `${window.location.origin}/login` : ''} />
        
        {/* Open Graph */}
        <meta property="og:title" content="GymsSync Login - Access Your Gym Management Dashboard" />
        <meta property="og:description" content="Login to GymsSync - Best gym management software in India. Access your dashboard to manage members, attendance, payments, and more." />
        <meta property="og:url" content={typeof window !== 'undefined' ? `${window.location.origin}/login` : ''} />
        <meta property="og:type" content="website" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="GymsSync Login - Access Your Gym Management Dashboard" />
        <meta name="twitter:description" content="Login to GymsSync - Best gym management software in India." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            'name': 'GymsSync Login',
            'description': 'Login page for GymsSync gym management software',
            'url': typeof window !== 'undefined' ? `${window.location.origin}/login` : '',
            'isPartOf': {
              '@type': 'WebSite',
              'name': 'GymsSync',
              'url': typeof window !== 'undefined' ? window.location.origin : ''
            }
          })}
        </script>
      </Helmet>
      <div className="auth-container">
      <div className="auth-card">
        <div className="auth-title">
          <LogIn size={32} style={{ marginBottom: '0.5rem' }} />
          <div>GymsSync</div>
          <div style={{ fontSize: '0.875rem', color: '#6B7280', marginTop: '0.25rem', fontWeight: 'normal' }}>
            Sync Your Gym, Elevate Your Business
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div style={{ color: '#dc3545', fontSize: '0.9rem', textAlign: 'center' }}>
              {error}
            </div>
          )}
          
          <div className="form-group">
            <label className="form-label" style={{ color: '#1a1a1a', fontWeight: 600 }}>Email</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{ color: '#333', backgroundColor: '#fff' }}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label" style={{ color: '#1a1a1a', fontWeight: 600 }}>Password</label>
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{ 
                  paddingRight: '2.75rem',
                  width: '100%',
                  color: '#333',
                  backgroundColor: '#fff'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'color 0.2s',
                  zIndex: 1,
                  outline: 'none'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#667eea'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        
        <div className="auth-link">
          <Link to="/forgot-password">Forgot Password?</Link>
        </div>
      </div>

      {/* OTP Modal */}
      {showOtpModal && (
        <OtpModal
          email={pendingEmail}
          password={pendingPassword}
          onVerify={handleOtpVerify}
          onResend={handleResendOtp}
          onCancel={handleCancelOtp}
          loading={isLoading}
          error={otpError}
        />
      )}
    </div>
    </>
  );
};

export default Login;
